import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './useAuth.jsx';
import { getAccessToken } from '../api/client.js';

const SocketContext = createContext(null);

/**
 * Maintains a single Socket.io connection for the lifetime of the user session.
 *
 * Connects when authenticated, disconnects on logout. After a token refresh,
 * sends `re-auth` to the server with the new token instead of fully reconnecting,
 * so we don't churn the connection on every 15-minute refresh.
 *
 * Exposes:
 *   - `connected`  — boolean, true when the socket handshake has completed
 *   - `subscribe(eventName, handler)` — adds a listener; returns an unsubscribe fn
 */
export function SocketProvider({ children }) {
    const { user } = useAuth();
    const socketRef = useRef(null);
    const [connected, setConnected] = useState(false);

    // Connect once user is loaded; only managers and admins get presence sockets
    useEffect(() => {
        if (!user) return;
        if (user.role !== 'admin' && user.role !== 'manager') return;

        const token = getAccessToken();
        if (!token) return;

        const sock = io({
            auth: { token },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 10000,
        });
        socketRef.current = sock;

        sock.on('connect', () => setConnected(true));
        sock.on('disconnect', () => setConnected(false));
        sock.on('connect_error', (err) => {
            if (err.message !== 'UNAUTHENTICATED') {
                console.warn('[socket] connect error:', err.message);
            }
        });
        sock.on('forced-disconnect', (data) => {
            console.warn('[socket] server requested disconnect:', data?.reason);
        });

        return () => {
            sock.disconnect();
            socketRef.current = null;
            setConnected(false);
        };
    }, [user?.id, user?.role]);

    // Listen for token-refreshed events from the API client and re-auth the socket
    useEffect(() => {
        const onRefresh = () => {
            const newToken = getAccessToken();
            if (!newToken || !socketRef.current?.connected) return;
            socketRef.current.emit('re-auth', { token: newToken }, (ack) => {
                if (!ack?.ok) console.warn('[socket] re-auth failed:', ack?.error);
            });
        };
        window.addEventListener('starsapt:token-refreshed', onRefresh);
        return () => window.removeEventListener('starsapt:token-refreshed', onRefresh);
    }, [connected]);

    const subscribe = useCallback((event, handler) => {
        const s = socketRef.current;
        if (!s) return () => {};
        s.on(event, handler);
        return () => { s.off(event, handler); };
    }, []);

    return (
        <SocketContext.Provider value={{ connected, subscribe }}>
            {children}
        </SocketContext.Provider>
    );
}

export function useSocket() {
    const ctx = useContext(SocketContext);
    if (!ctx) throw new Error('useSocket must be used within SocketProvider');
    return ctx;
}
